/**
 * SearchBar.js - the search input. Results update as you type (useSearch);
 * there is no autocomplete: /v1/search has none and the live results are the
 * suggestions.
 */
import React, { memo } from 'react';

const SearchBar = ({
  searchState,
  className = '',
  placeholder = 'Search fights, fighters and events...',
  size = 'default', // 'small', 'default', 'large'
}) => {
  const {
    query,
    isRealTimeSearch,
    handleSearchChange,
    handleSearchSubmit,
    clearSearch,
    searchInputRef,
  } = searchState;

  // Get size classes
  const sizeClasses = {
    small: 'px-3 py-2 text-sm',
    default: 'px-4 py-3',
    large: 'px-5 py-4 text-lg',
  };

  return (
    <div className={`relative w-full max-w-md mx-auto ${className}`}>
      <form onSubmit={handleSearchSubmit} className="w-full">
        <div className="relative flex items-center">
          {/* Search Icon */}
          <div className="absolute z-10 left-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5 transition-colors duration-200 text-white/50"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          {/* Search Input */}
          <input
            id="search-input"
            name="q"
            ref={searchInputRef}
            type="text"
            className={`w-full pl-10 pr-12 rounded-full border transition-all duration-200 text-white placeholder-white/50 focus:outline-none focus:border-white/40 focus:bg-black/40 focus:shadow-lg focus:shadow-white/10 border-white/20 bg-black/20 font-body ${
              sizeClasses[size]
            } ${isRealTimeSearch ? 'bg-black/30' : ''}`}
            placeholder={placeholder}
            value={query}
            onChange={handleSearchChange}
            aria-label="Search input"
            autoComplete="off"
            spellCheck="false"
            aria-expanded={false}
            aria-haspopup="false"
            role="searchbox"
          />

          {/* Real-time Search Indicator */}
          {isRealTimeSearch && (
            <div className="absolute -translate-y-1/2 right-12 top-1/2">
              <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
            </div>
          )}

          {/* Clear Button */}
          {query && (
            <button
              type="button"
              onClick={e => {
                e.preventDefault();
                clearSearch();
              }}
              className="absolute p-1 transition-colors duration-200 -translate-y-1/2 right-3 top-1/2 text-white/50 hover:text-white focus:outline-none focus:text-accent"
              aria-label="Clear search"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

SearchBar.displayName = 'SearchBar';

export default memo(SearchBar);
